import axios from "axios";
import { getAuthToken } from "utils/authToken";

if (process.env.NODE_ENV === "production" && !process.env.REACT_APP_API_BASE_URL) {
  throw new Error("REACT_APP_API_BASE_URL is not set — refusing to start in production");
}

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8000",
});

apiClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

const PUBLIC_PATHS = [
  "/api/users/login",
  "/api/users/signup",
  "/api/users/request-password-reset",
  "/api/users/reset-password",
];

let isRedirecting = false;

// ── transport retry ────────────────────────────────────────────────────────
//
// A request that fails with no response at all (`err.response` undefined —
// ERR_CONNECTION_CLOSED, ERR_NETWORK, a dropped keep-alive) never reached a
// handler, so repeating it cannot double-apply anything the first attempt
// did. Requests that *did* reach the server come back with a status and are
// never retried here.
//
// Retrying is opt-in per request rather than blanket, because "no response"
// is not the same as "not delivered": the server can accept a request and
// then lose the connection while replying. That is harmless for a save (it
// sets a flag to the same value) and not harmless for a completion (it awards
// XP), so the caller states which one it is. GETs are always safe.
const RETRIES = 2;
const RETRY_BASE_MS = 300;

function shouldRetry(err) {
  // A status means it reached a handler; a timeout or an abort is a decision
  // someone already made about this request, not a transport failure.
  if (err.response || err.code === "ECONNABORTED" || err.code === "ERR_CANCELED") return false;
  const config = err.config;
  if (!config) return false;
  const method = (config.method || "get").toLowerCase();
  return method === "get" || config.idempotent === true;
}

apiClient.interceptors.response.use(
  (response) => response,
  async (err) => {
    if (shouldRetry(err)) {
      const config = err.config;
      config._retryCount = (config._retryCount || 0) + 1;
      if (config._retryCount <= RETRIES) {
        await new Promise((r) => setTimeout(r, RETRY_BASE_MS * config._retryCount));
        return apiClient(config);
      }
    }
    const isPublic = PUBLIC_PATHS.some((p) => err.config?.url?.startsWith(p));
    const status = err.response?.status;
    const url = err.config?.url || "";
    const isUserGone = status === 404 && url.includes("/me");
    if ((status === 401 || isUserGone) && !isPublic && !isRedirecting) {
      isRedirecting = true;
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = "/authentication/sign-in";
    }
    return Promise.reject(err);
  }
);

export default apiClient;
